import React, { useEffect, useState } from 'react';
import { IconSettings } from '@tabler/icons-react';
import {
  Button,
  Flex,
  Popover,
  Separator,
  Text,
  TextField,
} from '@/components/ui';
import type { DemoDataSettings } from '../types/demoDataSettings';
import {
  parseSubjectIdTemplate,
  validateSubjectIdTemplate,
} from '../utils/subjectIdTemplateParser';

interface DemoDataSettingsPopoverProps {
  settings: DemoDataSettings;
  onSettingsChange: (settings: DemoDataSettings) => void;
}

/**
 * DemoDataSettingsPopover - Popover component for configuring demo data generation settings
 */
export const DemoDataSettingsPopover: React.FC<
  DemoDataSettingsPopoverProps
> = ({ settings, onSettingsChange }) => {
  const [open, setOpen] = useState(false);
  const [clinicalSiteIdsInput, setClinicalSiteIdsInput] = useState(
    settings.clinicalSiteIds.join(', ')
  );
  const [subjectIdTemplate, setSubjectIdTemplate] = useState(
    settings.subjectIdTemplate
  );
  const [numberOfRows, setNumberOfRows] = useState(settings.numberOfRows);
  const [startSubjectNumber, setStartSubjectNumber] = useState(
    settings.startSubjectNumber
  );
  const [templateError, setTemplateError] = useState<string | undefined>();

  // Update local state when settings prop changes
  useEffect(() => {
    setClinicalSiteIdsInput(settings.clinicalSiteIds.join(', '));
    setSubjectIdTemplate(settings.subjectIdTemplate);
    setNumberOfRows(settings.numberOfRows);
    setStartSubjectNumber(settings.startSubjectNumber);
  }, [settings]);

  const handleClinicalSiteIdsChange = (value: string) => {
    setClinicalSiteIdsInput(value);
  };

  const handleSubjectIdTemplateChange = (value: string) => {
    setSubjectIdTemplate(value);
    const validation = validateSubjectIdTemplate(value);
    if (!validation.isValid) {
      setTemplateError(validation.error);
    } else {
      setTemplateError(undefined);
    }
  };

  const handleNumberOfRowsChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setNumberOfRows(numValue);
    }
  };

  const handleStartSubjectNumberChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setStartSubjectNumber(numValue);
    }
  };

  const handleApply = () => {
    // Validate template before applying
    const validation = validateSubjectIdTemplate(subjectIdTemplate);
    if (!validation.isValid) {
      setTemplateError(validation.error);
      return;
    }

    // Parse clinical site IDs
    const siteIds = clinicalSiteIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    if (siteIds.length === 0) {
      return;
    }

    // Apply settings
    onSettingsChange({
      clinicalSiteIds: siteIds,
      subjectIdTemplate,
      numberOfRows,
      startSubjectNumber,
    });

    setOpen(false);
  };

  // Generate preview example
  const previewExample = React.useMemo(() => {
    if (templateError) {
      return '';
    }

    const siteIds = clinicalSiteIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    if (siteIds.length === 0 || !subjectIdTemplate) {
      return '';
    }

    try {
      const firstSiteId = siteIds[0];
      return parseSubjectIdTemplate(
        subjectIdTemplate,
        firstSiteId,
        startSubjectNumber
      );
    } catch {
      return '';
    }
  }, [
    clinicalSiteIdsInput,
    subjectIdTemplate,
    templateError,
    startSubjectNumber,
  ]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          size="small"
          variant="outlined"
          color="default"
          // isIconOnl
          title="Demo data settings"
        >
          <IconSettings />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        size="medium"
        width="450px"
        side="bottom"
        align="end"
        style={{ padding: '16px', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <Flex direction="column" gap="4">
          <Text size="4" weight="bold">
            Demo Data Settings
          </Text>

          {/* Clinical Site IDs */}
          <TextField
            label="Clinical Site IDs"
            placeholder="1, 2, 3 or 101, 102, 103"
            value={clinicalSiteIdsInput}
            onChange={e => handleClinicalSiteIdsChange(e.target.value)}
            helpText="Enter site IDs separated by commas. Subjects will be evenly distributed across sites."
            size="small"
          />

          {/* Subject ID Template */}
          <div>
            <TextField
              label="Subject ID Template"
              placeholder="10-$&-$nnn"
              value={subjectIdTemplate}
              onChange={e => handleSubjectIdTemplateChange(e.target.value)}
              error={!!templateError}
              errorMessage={templateError}
              helpText="Use $& for site ID and $n, $nn, $nnn etc. for increment with padding."
              size="small"
            />
            {previewExample && !templateError && (
              <Text size="2" color="gray" mt="1">
                Preview: <strong>{previewExample}</strong>
              </Text>
            )}
          </div>

          {/* Number of Rows */}
          <TextField
            label="Number of Rows"
            type="number"
            placeholder="25"
            value={numberOfRows.toString()}
            onChange={e => handleNumberOfRowsChange(e.target.value)}
            helpText="Total number of demo data rows to generate"
            size="small"
            min={1}
          />

          {/* Start Subject Number */}
          <TextField
            label="Start Subject Number"
            type="number"
            placeholder="1"
            value={startSubjectNumber.toString()}
            onChange={e => handleStartSubjectNumberChange(e.target.value)}
            helpText="Starting number for subject increment (e.g., if you already have 20 subjects, set this to 21)"
            size="small"
            min={1}
          />

          <Separator />

          {/* Actions */}
          <Flex justify="end" gap="2">
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="classic"
              size="small"
              color="primary"
              onClick={handleApply}
              disabled={
                !!templateError || numberOfRows < 1 || startSubjectNumber < 1
              }
            >
              Apply
            </Button>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};
